const availabilityCache = new Map()
const streetNumberCollator = new Intl.Collator('de-DE', {
  numeric: true,
  sensitivity: 'base'
})

export function filterAvailabilityOptions (options, query = '') {
  const normalizedQuery = String(query).trim().toLocaleLowerCase('de-DE')

  return [...options]
    .filter(option => !normalizedQuery || String(option.text || '').toLocaleLowerCase('de-DE').includes(normalizedQuery))
    .sort((left, right) => streetNumberCollator.compare(String(left.text || ''), String(right.text || '')))
}

export function sortPostalCodeOptionsByCity (options) {
  return [...options].sort((left, right) => {
    const cityComparison = streetNumberCollator.compare(String(left.city || ''), String(right.city || ''))
    if (cityComparison !== 0) return cityComparison

    return streetNumberCollator.compare(
      String(left.plz || left.value || ''),
      String(right.plz || right.value || '')
    )
  })
}

export function groupPostalCodeOptions (options) {
  const groups = new Map()

  sortPostalCodeOptionsByCity(options).forEach(option => {
    const city = String(option.city || '').trim() || 'Weitere Orte'
    if (!groups.has(city)) groups.set(city, [])
    groups.get(city).push(option)
  })

  return [...groups].map(([label, groupedOptions]) => ({
    label,
    options: groupedOptions
  }))
}

export function normalizeStreetName (street = '') {
  return street.replace('straße', 'str.').replace('Straße', 'Str.')
}

export function normalizeStreetNumberQuery (value = '') {
  return String(value).trim().toLocaleLowerCase('de-DE').replace(/\s+/g, '')
}

export async function getCachedAvailabilityData (http, url, ttl = 300000) {
  const now = Date.now()
  const cached = availabilityCache.get(url)

  if (cached && cached.expiresAt > now) {
    return cached.promise
  }

  const promise = http.get(url)
    .then(response => response.data)
    .catch(error => {
      availabilityCache.delete(url)
      throw error
    })

  availabilityCache.set(url, {
    expiresAt: now + ttl,
    promise
  })

  return promise
}

export function mapStreetNumberOption (address) {
  const suffix = address.suffix || ''
  const checkoutLink = address.checkoutLinkHost
    ? {
      host: address.checkoutLinkHost,
      path: address.checkoutLinkPath || '',
      query: address.checkoutLinkQuery || ''
    }
    : null

  return {
    text: `${address.streetnumber}${suffix ? ` ${suffix}` : ''}`,
    value: address.id,
    details: {
      id: address.id,
      street: address.street,
      streetnumber: address.streetnumber,
      suffix,
      plz: address.plz,
      city: address.city,
      vdsl: address.vdsl === true || address.vdsl === 1 || address.vdsl === '1',
      checkoutLink,
      customergroup: address.customerGroupId
        ? { groupId: address.customerGroupId }
        : null
    }
  }
}

export function sortStreetNumberOptions (options) {
  return [...options].sort((left, right) => streetNumberCollator.compare(left.text, right.text))
}

export function buildCheckoutUrl (address, { legacy = false, vdsl = false } = {}) {
  if (!address || !address.checkoutLink) {
    return ''
  }

  const customerGroupId = address.customergroup?.groupId || 133
  const checkoutLink = address.checkoutLink
  const host = String(checkoutLink.host || '').replace(/\/+$/, '')
  const path = String(checkoutLink.path || '').replace(/^\/+/, '')
  const baseUrl = vdsl
    ? 'https://bestellen.tkrz.de/vdsl'
    : `${host}/${path}`

  if (!host && !vdsl) {
    return ''
  }

  try {
    const url = new URL(baseUrl)
    if (!['http:', 'https:'].includes(url.protocol)) {
      return ''
    }
    const configuredQuery = new URLSearchParams(String(checkoutLink.query || '').replace(/^\?/, ''))

    configuredQuery.forEach((value, key) => url.searchParams.append(key, value))

    if (legacy) {
      url.searchParams.set('street', vdsl ? address.street : normalizeStreetName(address.street))
      url.searchParams.set('houseNumber', `${address.streetnumber || ''}${address.suffix || ''}`)
      url.searchParams.set('postalCode', address.plz || '')
      url.searchParams.set('city', address.city || '')
    } else {
      url.searchParams.set('id', address.id)
    }

    url.searchParams.set('kundengruppeid', customerGroupId)

    return url.toString()
  } catch (error) {
    return ''
  }
}
